import React, { useEffect, useState } from "react";
import { api, V1 } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { CheckCircle2, XCircle, FileText, School, Mail, Phone, ExternalLink } from "lucide-react";
import { toast } from "sonner";

export default function AdminSchoolSignups() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("pending");
  const [busy, setBusy] = useState({});
  const [rejectId, setRejectId] = useState(null);
  const [rejectReason, setRejectReason] = useState("");

  const load = async () => {
    const { data } = await api.get(`${V1}/admin/school-signups?status=${status}`);
    setItems(data || []);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const approve = async (id) => {
    setBusy((s) => ({ ...s, [id]: true }));
    try {
      const { data } = await api.post(`${V1}/admin/school-signups/${id}/approve`);
      toast.success(`Okul oluşturuldu. ${data.credential_note || ""}`, { duration: 15000 });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "İşlem başarısız"); }
    finally { setBusy((s) => ({ ...s, [id]: false })); }
  };

  const reject = (id) => {
    setRejectId(id);
    setRejectReason("");
  };

  const confirmReject = async () => {
    if (!rejectId) return;
    setBusy((s) => ({ ...s, [rejectId]: true }));
    try {
      const { data } = await api.post(`${V1}/admin/school-signups/${rejectId}/reject`, { reason: rejectReason.trim() });
      if (data.email_sent) toast.success("Başvuru reddedildi ve bilgi e-postası gönderildi ✉️");
      else toast.warning(`Reddedildi. E-posta gönderilemedi: ${data.email_error || "?"}`);
      setRejectId(null); setRejectReason("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "İşlem başarısız"); }
    finally { setBusy((s) => ({ ...s, [rejectId]: false })); }
  };

  return (
    <div className="space-y-6" data-testid="admin-signups-page">
      <div>
        <div className="overline text-accent">Onboarding</div>
        <h1 className="font-display text-3xl font-bold flex items-center gap-2"><School className="h-7 w-7" /> Okul Kayıt Başvuruları</h1>
      </div>
      <Tabs value={status} onValueChange={setStatus}>
        <TabsList>
          <TabsTrigger value="pending" data-testid="signups-tab-pending">Bekleyen</TabsTrigger>
          <TabsTrigger value="approved" data-testid="signups-tab-approved">Onaylı</TabsTrigger>
          <TabsTrigger value="rejected" data-testid="signups-tab-rejected">Reddedilen</TabsTrigger>
        </TabsList>
      </Tabs>
      <div className="space-y-3">
        {items.length === 0 && <div className="p-12 text-center text-muted-foreground bg-card border border-border rounded-xl">Kayıt yok.</div>}
        {items.map((s) => (
          <div key={s.id} className="p-5 rounded-xl border border-border bg-card" data-testid={`signup-row-${s.id}`}>
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="flex-1 min-w-[280px]">
                <div className="font-display font-bold text-lg">{s.school_name}</div>
                <div className="text-xs text-muted-foreground">{s.province} / {s.district}</div>
                <div className="text-sm mt-2">{s.short_intro}</div>
                <div className="mt-2 flex flex-wrap gap-3 text-xs">
                  <span className="flex items-center gap-1"><Mail className="h-3.5 w-3.5" /> {s.contact_email}</span>
                  <span className="flex items-center gap-1"><Phone className="h-3.5 w-3.5" /> {s.contact_phone}</span>
                  <span>İlgili: <b>{s.contact_name}</b></span>
                  {s.website && <a href={s.website} target="_blank" rel="noreferrer" className="text-primary hover:underline inline-flex items-center gap-1">Web sitesi <ExternalLink className="h-3 w-3" /></a>}
                  {s.document_url && <a href={s.document_url} target="_blank" rel="noreferrer" className="text-primary hover:underline inline-flex items-center gap-1"><FileText className="h-3 w-3" /> Belge</a>}
                </div>
                {s.credential_note && (
                  <div className="mt-3 p-2 rounded bg-success/10 text-success text-xs font-mono" data-testid={`credential-${s.id}`}>{s.credential_note}</div>
                )}
                {s.email_sent === true && (
                  <div className="mt-2 text-[11px] text-success flex items-center gap-1">
                    <span>✉️</span> Onay e-postası gönderildi{s.email_id ? ` (id: ${s.email_id.slice(0, 8)}…)` : ""}
                  </div>
                )}
                {s.email_sent === false && s.email_error && (
                  <div className="mt-2 text-[11px] text-destructive">✉️ E-posta gönderilemedi: {s.email_error}</div>
                )}
              </div>
              {status === "pending" && (
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => approve(s.id)} disabled={busy[s.id]} data-testid={`approve-signup-${s.id}`}>
                    <CheckCircle2 className="h-4 w-4" /> Onayla
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => reject(s.id)} data-testid={`reject-signup-${s.id}`}>
                    <XCircle className="h-4 w-4" /> Reddet
                  </Button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!rejectId} onOpenChange={(o) => !o && setRejectId(null)}>
        <DialogContent className="max-w-md" data-testid="reject-signup-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><XCircle className="h-5 w-5 text-destructive" /> Başvuruyu Reddet</DialogTitle>
            <DialogDescription>Kullanıcıya iletilecek ret sebebini yazın. E-posta olarak gönderilecektir.</DialogDescription>
          </DialogHeader>
          <Textarea
            rows={4}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="Örn: Belirttiğiniz bilgiler eksik. Lütfen MEB izin belgesi ve güncel telefon numarasıyla tekrar başvurun."
            data-testid="reject-signup-reason-input"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectId(null)}>Vazgeç</Button>
            <Button variant="destructive" onClick={confirmReject} disabled={busy[rejectId]} data-testid="confirm-reject-signup-btn">
              <XCircle className="h-4 w-4" /> Reddet ve E-posta Gönder
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
